import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { strapiApiUrlImage } from 'src/config';
import ArticleCard from '../../components/_new/article/card';
import ArticleMeta from '../../components/_new/article/meta';
import ContentContainer from '../../components/_new/containers/content-container';
import PaginationComponent from '../../components/_new/pagination';
import Description from '../../components/_new/text/description';
import Title from '../../components/_new/text/title';
import { PageLayout } from '../../components/layout/pageLayout.component';
import type { ArticlesProps } from '../../pages/blog';
import type { Article } from '../../types/article';
import type { PaginatedResponse } from '../../types/common';
import { cn } from '../../utils/cn';

function ArticlesPage(props: ArticlesProps) {
  const { t } = useTranslation(['blog']);

  return (
    <PageLayout
      fullWidth
      title={t('blog:seo.title')}
      description={t('blog:seo.description')}
      countries={props.countries}
      className="[&&]:max-sm:pb-0"
    >
      <MainArticleComponent article={props.mainArticle} />

      {props.articles && <ArticlesComponent articles={props.articles} />}
    </PageLayout>
  );
}

const MainArticleComponent = ({
  className,
  article,
}: {
  className?: string;
  article: Article | null;
}) => {
  const title = 'Blog';
  const description =
    'Updates, musing, and reflections from the The Population Project team';

  return (
    <div className={clsx('bg-neutral-50', className)}>
      <ContentContainer>
        <div>
          <Title payload={title} />
          <Description payload={description} className="mt-3 max-sm:mt-2" />
        </div>

        {article && (
          <Link
            href={`/blog/${article.slug}`}
            className={cn(
              'bg-white flex gap-16 items-center rounded-2xl p-4 mt-[3.25rem] max-sm:mt-6',
              'shadow-[0px_2px_8px_-3px_rgba(15,25,40,0.10),0px_6px_20px_-5px_rgba(12,39,36,0.10)]',
              'max-sm:flex-col max-sm:gap-4'
            )}
          >
            <div className="h-[25rem] basis-7/12 overflow-hidden rounded-lg bg-neutral-300 object-cover max-sm:h-56 max-sm:w-full max-sm:basis-auto">
              <img
                className="size-full object-cover"
                src={`${strapiApiUrlImage}${article.image.data?.attributes.url}`}
                alt=""
              />
            </div>

            <div className="basis-5/12 pr-12 max-sm:basis-auto max-sm:pr-0 max-sm:pt-4">
              <div className="text-neutal-950 mb-2 text-heading-6-semibold max-sm:text-title-1-semibold">
                {article.title}
              </div>

              <div className="line-clamp-3 text-label-1-regular text-neutral-600 max-sm:text-label-2-regular">
                {article.short_text}
              </div>

              <ArticleMeta article={article} className="mt-4" />
            </div>
          </Link>
        )}
      </ContentContainer>
    </div>
  );
};

const ArticlesComponent = ({
  articles,
}: {
  articles: PaginatedResponse<Article[]>;
}) => {
  const router = useRouter();

  const changePage = (currentPage: number) => {
    return router.push({ query: { page: currentPage } });
  };

  return (
    <ContentContainer className="flex flex-col">
      <div className="grid grid-cols-4 gap-7 max-sm:grid-cols-1 max-sm:gap-5">
        {articles.data.map((article) => (
          <ArticleCard
            article={article}
            href={`/blog/${article.slug}`}
            key={article.slug}
          />
        ))}
      </div>

      <PaginationComponent
        currentPage={Number(router.query.page)}
        setCurrentPage={changePage}
        meta={articles.meta.pagination}
        className="mt-[3.75rem] max-sm:mt-8"
      />
    </ContentContainer>
  );
};

export default ArticlesPage;
